// Appen: inloggning, startsidan med alla platser, platsens Hälsa och Händelser, "Åka till …",
// "Resa bort" och Konto. Vyn väljs med adressens #, så att den går att länka till och fungerar
// som panel på surfplattan. Länken i inloggningsmejlet öppnar /logga-in#<token>.
//
// Adresserna (b3):
//   #start                     startsidan (standard)
//   #plats/<id>/halsa          platsens Hälsa, och #plats/<id>/handelser dess Händelser
//   #halsa, #handelser         den senast öppnade platsen, eller den första (som i b2)
//   #aka-till, #resa-bort      formulären
//   #konto                     kontot
//
// b5:
//   #ny-plats                  lägg till en plats (för ägare)
//   #plats/<id>/installningar  platsens inställningar (bara för ägaren)
//
// b12:
//   #plats/<id>/resor          båtens resor, förbrukningen och provkörningarna
//   #plats/<id>/resor/<id>     en resa
//
// b15:
//   #plats/<id>/kom-igang      guiden "Kom igång" (bara för ägaren)

import {
  type Halsa,
  type Handelse,
  type Oversikt,
  type Plantyp,
  type Svar,
  sedan,
} from "@batkoll/gemensam";
import { useCallback, useEffect, useState } from "preact/hooks";
import { api, InteInloggad, type Jag, type Konfig } from "./api.ts";
import { foljVersionen, laddaOm } from "./version.ts";
import { Fardsidevy, Fardvy } from "./vyer/farder.tsx";
import { Guidevy } from "./vyer/guiden.tsx";
import { Halsovy } from "./vyer/halsa.tsx";
import { Handelsevy } from "./vyer/handelser.tsx";
import { Husvy, Planer } from "./vyer/hus.tsx";
import { Inloggningsvy, Lankvy } from "./vyer/inloggning.tsx";
import { Installningsvy } from "./vyer/installningar.tsx";
import { Kontovy, Notisfraga, Passkeyfraga } from "./vyer/konto.tsx";
import { Nyplatsvy } from "./vyer/nyplats.tsx";
import { Resvy } from "./vyer/resa.tsx";
import { Startsida } from "./vyer/start.tsx";

type Vy =
  | { sida: "start" }
  | { sida: "halsa" | "handelser"; plats: string | null }
  | { sida: "installningar"; plats: string }
  | { sida: "kom-igang"; plats: string }
  | { sida: "resor"; plats: string; fard: string | null }
  | { sida: "ny-plats" }
  | { sida: "konto" }
  | { sida: "resa"; typ: Plantyp };

const UPPDATERA_MS = 5000;

function vyFranAdress(): Vy {
  const h = location.hash;
  if (h === "#konto") return { sida: "konto" };
  if (h === "#aka-till") return { sida: "resa", typ: "aka_till" };
  if (h === "#resa-bort") return { sida: "resa", typ: "resa_bort" };
  if (h === "#halsa") return { sida: "halsa", plats: null };
  if (h === "#handelser") return { sida: "handelser", plats: null };
  if (h === "#ny-plats") return { sida: "ny-plats" };
  const r = /^#plats\/([0-9a-f-]{36})\/resor(?:\/([0-9a-f-]{36}))?$/.exec(h);
  if (r?.[1]) return { sida: "resor", plats: r[1], fard: r[2] ?? null };
  const m = /^#plats\/([0-9a-f-]{36})(?:\/(halsa|handelser|installningar|kom-igang))?$/.exec(h);
  if (m?.[1] && m[2] === "installningar") return { sida: "installningar", plats: m[1] };
  if (m?.[1] && m[2] === "kom-igang") return { sida: "kom-igang", plats: m[1] };
  if (m?.[1]) return { sida: m[2] === "handelser" ? "handelser" : "halsa", plats: m[1] };
  return { sida: "start" };
}

/** Token i länken från mejlet, om adressen är /logga-in#<token>. */
function lankensToken(): string | null {
  return location.pathname === "/logga-in" && location.hash.length > 1
    ? location.hash.slice(1)
    : null;
}

function Navstatus(props: { senast: number | null; nu: number }) {
  if (props.senast === null) return <span class="navstatus okand">Navet har inte hörts av</span>;
  const alder = props.nu - props.senast;
  const klass = alder < 3 * 60_000 ? "ok" : alder < 15 * 60_000 ? "sen" : "tyst";
  return (
    <span class={`navstatus ${klass}`}>
      <span class="prick" aria-hidden="true" />
      Navet hördes av {sedan(props.senast, props.nu)}
    </span>
  );
}

export function App() {
  const [jag, setJag] = useState<Jag | null | "laddar">("laddar");
  const [konfig, setKonfig] = useState<Konfig | null>(null);
  const [token, setToken] = useState<string | null>(lankensToken());
  const [vy, setVy] = useState<Vy>(vyFranAdress());
  const [senastPlats, setSenastPlats] = useState<string | null>(null);
  const [oversikt, setOversikt] = useState<Oversikt | null>(null);
  const [halsa, setHalsa] = useState<Halsa | null>(null);
  const [handelser, setHandelser] = useState<Handelse[]>([]);
  const [fel, setFel] = useState<string | null>(null);
  const [nu, setNu] = useState(Date.now());
  const [sparar, setSparar] = useState<string | null>(null);
  // b20: en ny version efter en driftsättning (version.ts).
  const [nyVersion, setNyVersion] = useState<string | null>(null);
  useEffect(() => foljVersionen(setNyVersion), []);
  const platser = jag && jag !== "laddar" ? jag.platser : [];
  const valdPlats =
    vy.sida === "halsa" ||
    vy.sida === "handelser" ||
    vy.sida === "installningar" ||
    vy.sida === "kom-igang" ||
    vy.sida === "resor"
      ? (vy.plats ?? senastPlats)
      : senastPlats;
  const plats = platser.find((p) => p.id === valdPlats) ?? platser[0];
  const visarPlats =
    vy.sida === "halsa" ||
    vy.sida === "handelser" ||
    vy.sida === "installningar" ||
    vy.sida === "kom-igang" ||
    vy.sida === "resor";

  const hanteraFel = useCallback((e: unknown) => {
    if (e instanceof InteInloggad) setJag(null);
    else setFel("Molnet svarar inte just nu. Försöker igen …");
  }, []);

  const hamtaJag = useCallback(async () => {
    try {
      setJag(await api.jag());
    } catch (e) {
      if (e instanceof InteInloggad) setJag(null);
      else setFel("Molnet svarar inte.");
    }
  }, []);

  useEffect(() => {
    void hamtaJag();
    api
      .konfig()
      .then(setKonfig)
      .catch(() => {});
    const vidAndring = () => setVy(vyFranAdress());
    addEventListener("hashchange", vidAndring);
    return () => removeEventListener("hashchange", vidAndring);
  }, []);

  useEffect(() => {
    if (visarPlats && plats && plats.id !== senastPlats) {
      setSenastPlats(plats.id);
      setHalsa(null);
      setHandelser([]);
    }
  }, [visarPlats, plats, senastPlats]);

  const uppdatera = useCallback(async () => {
    if (!jag || jag === "laddar") return;
    try {
      if (vy.sida === "start") {
        setOversikt(await api.oversikt());
      } else if (visarPlats && plats) {
        const [h, lista] = await Promise.all([api.halsa(plats.id), api.handelser(plats.id)]);
        setHalsa(h);
        setHandelser(lista);
      }
      setFel(null);
    } catch (e) {
      hanteraFel(e);
    }
    setNu(Date.now());
  }, [jag, vy.sida, visarPlats, plats, hanteraFel]);

  useEffect(() => {
    void uppdatera();
    const t = setInterval(uppdatera, UPPDATERA_MS);
    return () => clearInterval(t);
  }, [uppdatera]);

  async function inloggad() {
    setToken(null);
    setVy(vyFranAdress());
    await hamtaJag();
  }

  async function loggaUt() {
    await api.loggaUt().catch(() => {});
    setJag(null);
    setHalsa(null);
    setHandelser([]);
    setOversikt(null);
    history.replaceState(null, "", "/#start");
    setVy({ sida: "start" });
  }

  async function svara(id: string, svar: Svar) {
    if (!plats) return;
    setSparar(id);
    try {
      await api.svara(plats.id, id, svar);
      setHandelser((lista) => lista.map((h) => (h.id === id ? { ...h, svar } : h)));
    } catch (e) {
      hanteraFel(e);
    } finally {
      setSparar(null);
    }
  }

  /** Tar en plats som är ej i drift i drift igen, från kortet på startsidan (b6). */
  async function taIDrift(platsId: string) {
    try {
      await api.andraPlats(platsId, { iDrift: true });
      await Promise.all([uppdatera(), hamtaJag()]);
    } catch (e) {
      hanteraFel(e);
    }
  }

  async function taBortPlan(planId: string) {
    if (!plats) return;
    try {
      await api.taBortPlan(plats.id, planId);
      await uppdatera();
    } catch (e) {
      hanteraFel(e);
    }
  }

  const versionsruta = nyVersion && (
    <p class="banner ny-version" role="status">
      En ny version av appen finns –{" "}
      <button type="button" onClick={() => void laddaOm(nyVersion)}>
        Ladda om
      </button>
    </p>
  );

  if (token) {
    return (
      <>
        {versionsruta}
        <Lankvy token={token} inloggad={inloggad} />
      </>
    );
  }
  if (jag === "laddar") return <main class="laddar">Laddar …</main>;
  if (jag === null) {
    return (
      <>
        {versionsruta}
        <Inloggningsvy konfig={konfig} inloggad={inloggad} />
      </>
    );
  }

  const pagaende = handelser.filter((h) => h.avslutad === null && h.niva !== "info").length;
  const halsaFor = halsa && plats && halsa.plats.id === plats.id ? halsa : null;
  const platsLank = plats ? `#plats/${plats.id}` : "#start";
  const kanPlanera = platser.some((p) => p.roll === "agare");

  return (
    <div class="skal">
      <header class="topp">
        <div>
          {visarPlats && plats ? (
            <>
              <a class="tillbaka" href="#start">
                ← Alla platser
              </a>
              <h1>{plats.namn}</h1>
              {halsaFor && <Navstatus senast={halsaFor.navSenastHord} nu={nu} />}
              {plats.roll === "agare" && vy.sida !== "installningar" && (
                <a class="installningslank" href={`#plats/${plats.id}/installningar`}>
                  Inställningar
                </a>
              )}
            </>
          ) : (
            <h1>Båtkoll</h1>
          )}
        </div>
      </header>
      {versionsruta}
      {fel && (
        <p class="banner" role="status">
          {fel}
        </p>
      )}
      <main class="innehall">
        {vy.sida === "start" && konfig && (
          <div class="fragor">
            <Notisfraga konfig={konfig} jag={jag} />
            <Passkeyfraga jag={jag} uppdatera={hamtaJag} />
          </div>
        )}
        {vy.sida === "start" &&
          (oversikt ? (
            <Startsida
              o={oversikt}
              nu={nu}
              kanPlanera={kanPlanera}
              kanLaggaTill={kanPlanera}
              taIDrift={taIDrift}
            />
          ) : (
            <p class="laddar">Hämtar …</p>
          ))}
        {vy.sida === "halsa" &&
          (!plats ? (
            <p class="tomt">Ingen plats.</p>
          ) : halsaFor ? (
            <>
              {halsaFor.plats.typ === "bat" ? (
                <Halsovy h={halsaFor} nu={nu} />
              ) : (
                <Husvy h={halsaFor} nu={nu} />
              )}
              <div class="rutnat planrad">
                <Planer
                  planer={halsaFor.planer}
                  kanAndra={halsaFor.plats.roll === "agare"}
                  taBort={taBortPlan}
                />
              </div>
            </>
          ) : (
            <p class="laddar">Hämtar …</p>
          ))}
        {vy.sida === "handelser" && (
          <Handelsevy
            handelser={handelser}
            svara={svara}
            sparar={sparar}
            kanSvara={plats?.roll === "agare"}
            platsId={plats?.id ?? null}
          />
        )}
        {vy.sida === "resor" &&
          (vy.fard ? (
            <Fardsidevy platsId={vy.plats} fardId={vy.fard} nu={nu} />
          ) : (
            <Fardvy platsId={vy.plats} nu={nu} />
          ))}
        {vy.sida === "resa" && <Resvy typ={vy.typ} platser={platser} />}
        {vy.sida === "ny-plats" && <Nyplatsvy skapad={hamtaJag} />}
        {vy.sida === "kom-igang" && (
          <Guidevy platsId={vy.plats} konfig={konfig} klar={() => void hamtaJag()} />
        )}
        {vy.sida === "installningar" && (
          <Installningsvy
            platsId={vy.plats}
            nu={nu}
            andrat={hamtaJag}
            borta={() => {
              location.hash = "#start";
              void hamtaJag();
            }}
          />
        )}
        {vy.sida === "konto" && konfig && (
          <Kontovy jag={jag} konfig={konfig} uppdatera={hamtaJag} loggaUt={loggaUt} />
        )}
      </main>
      <nav class="flikar" aria-label="Vyer">
        <a href="#start" aria-current={vy.sida === "start" ? "page" : undefined}>
          Översikt
        </a>
        <a
          href={`${platsLank}/halsa`}
          aria-current={vy.sida === "halsa" || vy.sida === "resor" ? "page" : undefined}
        >
          Hälsa
        </a>
        <a
          href={`${platsLank}/handelser`}
          aria-current={vy.sida === "handelser" ? "page" : undefined}
        >
          Händelser
          {pagaende > 0 && <span class="raknare">{pagaende}</span>}
        </a>
        <a href="#konto" aria-current={vy.sida === "konto" ? "page" : undefined}>
          Konto
        </a>
      </nav>
    </div>
  );
}
